import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import { toast } from "react-toastify";
import useEditorMapa, { useTarifas } from "../../stores/useEditorMapa";
import { coloresDeTarifas } from "../../domain/mapa/mapa";
import { asignarTarifa } from "../../domain/mapa/asignar";
import { elementosDe } from "../../domain/mapa/seleccion";
import { COLORES_ELEGIBLES, colorJSON, colorLibre, crearTarifaNueva, editarTarifaNueva } from "../../domain/mapa/tarifasNuevas";
import { COLOR } from "./estilos";

const MAX_CONCEPTO = 250;

const dosDigitos = (n) => String(n).padStart(2, "0");
// para <input type="datetime-local">, en hora local
const aLocal = (fecha) => {
  const d = new Date(fecha);
  return `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}T${dosDigitos(d.getHours())}:${dosDigitos(d.getMinutes())}`;
};
const formatoFecha = (fecha) =>
  new Date(fecha).toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

const Etiqueta = ({ children }) => (
  <Typography component="label" sx={{ display: "block", m: 0, mb: 0.75, fontSize: 13, fontWeight: 600, color: COLOR.titulo }}>
    {children}
  </Typography>
);

const inicial = (tarifa, fechas, colores) => {
  const visibles = fechas.filter((f) => !f.oculta);
  const fin = fechas.reduce((max, f) => (new Date(f.hora_fin) > max ? new Date(f.hora_fin) : max), new Date(0));
  if (tarifa) {
    const hex = Object.entries(coloresDeTarifas([tarifa]))[0]?.[1];
    return {
      concepto: tarifa.concepto,
      precio: String(tarifa.precio ?? ""),
      color: hex,
      fechas: tarifa.fechas || [],
      inicio: aLocal(tarifa.inicio_venta || new Date()),
      fin: aLocal(tarifa.fin_venta || fin),
    };
  }
  return {
    concepto: "",
    precio: "",
    color: colorLibre(colores),
    fechas: (visibles.length ? visibles : fechas).map((f) => f.id),
    inicio: aLocal(new Date()),
    fin: aLocal(fin),
  };
};

// DialogoTarifaNueva: crea (o edita) una entrada que vive en el borrador y se
// crea al publicar. Si hay lugares elegidos, se le asignan al crearla.
export const DialogoTarifaNueva = ({ abierto, tarifa = null, onCerrar }) => {
  const fechas = useEditorMapa((s) => s.fechas);
  const seleccion = useEditorMapa((s) => s.seleccion);
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const tarifas = useTarifas();
  const recargarTarifas = useEditorMapa((s) => s.recargarTarifas);
  const [form, setForm] = useState(() => inicial(tarifa, fechas, coloresDeTarifas(tarifas)));
  const [intentado, setIntentado] = useState(false);

  // si el editor se abrió sin las fechas del evento, se piden y se completan
  // las fechas y el fin de la venta
  const sinFechas = fechas.length === 0;
  useEffect(() => {
    if (sinFechas) recargarTarifas();
  }, [sinFechas, recargarTarifas]);
  useEffect(() => {
    if (sinFechas || tarifa) return;
    const base = inicial(null, fechas, {});
    setForm((f) => (f.fechas.length === 0 ? { ...f, fechas: base.fechas, fin: base.fin } : f));
  }, [sinFechas, fechas, tarifa]);
  const editar = (campo) => (valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const finEvento = fechas.reduce((max, f) => (new Date(f.hora_fin) > max ? new Date(f.hora_fin) : max), new Date(0));
  const hayElegido = seleccion?.tipo === "sector" || elementosDe(seleccion).length > 0;

  const errores = {};
  if (!form.concepto.trim()) errores.concepto = "Poné un nombre.";
  else if (form.concepto.trim().length > MAX_CONCEPTO) errores.concepto = `Hasta ${MAX_CONCEPTO} caracteres.`;
  if (form.precio !== "" && (Number.isNaN(Number(form.precio)) || Number(form.precio) < 0)) errores.precio = "El precio no es válido.";
  if (sinFechas) errores.fechas = "Cargando las fechas del evento…";
  else if (form.fechas.length === 0) errores.fechas = "Elegí al menos una fecha.";
  if (!form.inicio || !form.fin || new Date(form.inicio) >= new Date(form.fin)) errores.venta = "El inicio tiene que ser antes del fin.";
  else if (fechas.length > 0 && new Date(form.fin) > finEvento) errores.venta = "La venta no puede terminar después del evento.";
  const valido = Object.keys(errores).length === 0;

  const guardar = (asignar) => {
    setIntentado(true);
    if (!valido) return;
    const datos = {
      concepto: form.concepto,
      precio: form.precio === "" ? 0 : Number(form.precio),
      color: colorJSON(form.color),
      fechas: form.fechas,
      inicio_venta: new Date(form.inicio).toISOString(),
      fin_venta: new Date(form.fin).toISOString(),
    };
    if (tarifa) {
      cambiar((m) => editarTarifaNueva(m, tarifa.id, datos));
      onCerrar();
      return;
    }
    let error = null;
    let clave = null;
    cambiar((m) => {
      const creada = crearTarifaNueva(m, datos);
      if (!asignar) return creada.mapa;
      const r = asignarTarifa(creada.mapa, seleccion, creada.id);
      if (r.error) {
        error = r.error;
        return creada.mapa;
      }
      clave = r.clave;
      return r.mapa;
    });
    if (error) toast.error(`La entrada se creó, pero no se pudo asignar: ${error}`);
    else if (clave) seleccionar({ tipo: "sector", clave });
    onCerrar();
  };

  const alternarFecha = (id) =>
    editar("fechas")(form.fechas.includes(id) ? form.fechas.filter((x) => x !== id) : [...form.fechas, id]);

  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: COLOR.titulo, pb: 0.5 }}>{tarifa ? "Editar entrada nueva" : "Nueva entrada"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.25, pt: "8px !important" }}>
        <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>
          Se crea al publicar el mapa. Lo demás (descripción, preguntas, venta por mesa) se configura después en Tarifas.
        </Typography>
        <Box>
          <Etiqueta>Nombre</Etiqueta>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Ej: Platea VIP"
            value={form.concepto}
            onChange={(e) => editar("concepto")(e.target.value)}
            error={intentado && Boolean(errores.concepto)}
            helperText={intentado && errores.concepto}
          />
        </Box>
        <Box>
          <Etiqueta>Precio</Etiqueta>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="0 = gratis"
            inputProps={{ min: 0 }}
            value={form.precio}
            onChange={(e) => editar("precio")(e.target.value)}
            error={intentado && Boolean(errores.precio)}
            helperText={intentado && errores.precio}
          />
        </Box>
        <Box>
          <Etiqueta>Color en el mapa</Etiqueta>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {COLORES_ELEGIBLES.map((c) => (
              <ButtonBase
                key={c}
                onClick={() => editar("color")(c)}
                aria-label={`Color ${c}`}
                sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: c, color: "#fff" }}
              >
                {form.color?.toLowerCase() === c.toLowerCase() && <Check sx={{ fontSize: 16 }} />}
              </ButtonBase>
            ))}
          </Box>
        </Box>
        {fechas.length > 1 && (
          <Box>
            <Etiqueta>Fechas</Etiqueta>
            {fechas.map((f) => (
              <FormControlLabel
                key={f.id}
                sx={{ display: "flex", m: 0 }}
                control={<Checkbox size="small" checked={form.fechas.includes(f.id)} onChange={() => alternarFecha(f.id)} />}
                label={
                  <Typography sx={{ fontSize: 14 }}>
                    {formatoFecha(f.fecha)}
                    {f.oculta ? " (oculta)" : ""}
                  </Typography>
                }
              />
            ))}
            {intentado && errores.fechas && <Typography sx={{ fontSize: 12, color: "error.main" }}>{errores.fechas}</Typography>}
          </Box>
        )}
        <Box>
          <Etiqueta>Venta</Etiqueta>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField size="small" type="datetime-local" label="Desde" InputLabelProps={{ shrink: true }} value={form.inicio} onChange={(e) => editar("inicio")(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" type="datetime-local" label="Hasta" InputLabelProps={{ shrink: true }} value={form.fin} onChange={(e) => editar("fin")(e.target.value)} sx={{ flex: 1 }} />
          </Box>
          {intentado && errores.venta && <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>{errores.venta}</Typography>}
        </Box>
      </DialogContent>
      {intentado && !valido && (
        <Typography sx={{ px: 3, fontSize: 13, color: "error.main" }}>{Object.values(errores)[0]}</Typography>
      )}
      <DialogActions sx={{ px: 3, pb: 2, gap: 0.5, "& .MuiButton-root": { textTransform: "none", whiteSpace: "nowrap" } }}>
        <Button onClick={onCerrar}>Cancelar</Button>
        {!tarifa && hayElegido && <Button onClick={() => guardar(false)}>Sólo crear</Button>}
        <Button variant="contained" disableElevation onClick={() => guardar(!tarifa && hayElegido)} sx={{ bgcolor: COLOR.acento }}>
          {tarifa ? "Guardar" : hayElegido ? "Crear y asignar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
